import { Injectable } from '@angular/core';
import { degrees, PDFDocument } from 'pdf-lib';

@Injectable({
  providedIn: 'root'
})
export class PdfImageService {

  async addImageToPdf(
    pdfFile: Blob,
    content: any,
    pageNumber: number,
    xPos: number,
    yPos: number,
    width: number,
    height: number,
    rotate: number = 0
  ): Promise<string | null> {
    try {
      const pdfBytes = await pdfFile.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
      let image: any;
      if (!content?.size) {
        const base64 = content.split(',')[1];
        const signBytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
        image = await pdfDoc.embedPng(signBytes);
      } else {
        const arrayBuffer = await content.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        if (content.type === 'image/jpeg' || content.type === 'image/jpg') {
          image = await pdfDoc.embedJpg(bytes);
        } else {
          image = await pdfDoc.embedPng(bytes);
        }
      }

      const pages = pdfDoc.getPages();
      const page = pages[pageNumber - 1];
      if (page) {
        if (rotate === 0) {
          page.drawImage(image, {
            x: xPos,
            y: yPos + 5,
            width,
            height,
          });
        } else {
          page.drawImage(image, {
            x: xPos + 70,
            y: yPos - 50,
            width,
            height,
            rotate: degrees(rotate),
          });
        }
        const modifiedPdfBytes = await pdfDoc.save();
        const blob = new Blob([new Uint8Array(modifiedPdfBytes)], { type: 'application/pdf' });
        return URL.createObjectURL(blob);
      }
    } catch (error) {
      console.error('error signing the PDF', error);
    }
    return null;
  }

  async addImageToSpecificPages(
    pdfFile: Blob,
    content: any,
    coordinates: { pageId: number; xPos: number; yPos: number; qrWidth?: number; qrHeight?: number; rotate?: number }[]
  ): Promise<string | null> {
    try {
      const pdfBytes = await pdfFile.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
      let image: any;
      if (!content?.size) {
        const base64 = content.split(',')[1];
        const signBytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
        image = await pdfDoc.embedPng(signBytes);
      } else {
        const arrayBuffer = await content.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        if (content.type === 'image/jpeg' || content.type === 'image/jpg') {
          image = await pdfDoc.embedJpg(bytes);
        } else {
          image = await pdfDoc.embedPng(bytes);
        }
      }

      const pages = pdfDoc.getPages();
      for (const coord of coordinates) {
        const page = pages[coord.pageId - 1];
        if (!page) continue;
        const width = coord.qrWidth ?? 40;
        const height = coord.qrHeight ?? 40;
        const rotate = coord.rotate ?? 0;
        if (rotate === 0) {
          page.drawImage(image, { x: coord.xPos, y: coord.yPos + 5, width, height });
        } else {
          page.drawImage(image, { x: coord.xPos + 70, y: coord.yPos - 50, width, height, rotate: degrees(rotate) });
        }
      }

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([new Uint8Array(modifiedPdfBytes)], { type: 'application/pdf' });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('error adding image to specific pages', error);
    }
    return null;
  }

  async addImageToAllPages(
    pdfFile: Blob,
    content: any,
    xPos: number,
    yPos: number,
    width: number,
    height: number,
    rotate: number = 0
  ): Promise<string | null> {
    try {
      const pdfBytes = await pdfFile.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
      let image: any;
      if (!content?.size) {
        const base64 = content.split(',')[1];
        const signBytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
        image = await pdfDoc.embedPng(signBytes);
      } else {
        const arrayBuffer = await content.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        if (content.type === 'image/jpeg' || content.type === 'image/jpg') {
          image = await pdfDoc.embedJpg(bytes);
        } else {
          image = await pdfDoc.embedPng(bytes);
        }
      }

      for (const page of pdfDoc.getPages()) {
        if (rotate === 0) {
          page.drawImage(image, { x: xPos, y: yPos + 5, width, height });
        } else {
          page.drawImage(image, { x: xPos + 70, y: yPos - 50, width, height, rotate: degrees(rotate) });
        }
      }

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([new Uint8Array(modifiedPdfBytes)], { type: 'application/pdf' });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('error adding image to all pages', error);
    }
    return null;
  }
}
